import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, apiErr, inr } from "@/lib/api";
import { useRealtime } from "@/context/RealtimeContext";
import StatusBadge from "@/components/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const NEXT = { Pending: "Confirmed", Confirmed: "Processing", Processing: "Packed", Packed: "Ready", Ready: "Out for delivery", "Out for delivery": "Delivered" };
const ALL = ["Pending", "Confirmed", "Processing", "Packed", "Ready", "Out for delivery", "Delivered", "Cancelled"];

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [tab, setTab] = useState("all");
  const [sel, setSel] = useState(null);
  const [retItems, setRetItems] = useState({});
  const [subs, setSubs] = useState([]);
  const { subscribe } = useRealtime();

  const load = useCallback(() => {
    const t = tab === "online" ? "?type=online" : tab === "pos" ? "?type=pos" : "";
    api.get(`/admin/orders${t}`).then((r) => setOrders(r.data));
  }, [tab]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => subscribe((m) => { if (m.event?.startsWith("order")) load(); }), [subscribe, load]);
  useEffect(() => {
    if (sel?.is_split) api.get(`/orders/${sel.id}/suborders`).then((r) => setSubs(r.data)).catch(() => setSubs([]));
    else setSubs([]);
  }, [sel]);

  const advance = async (o) => { try { await api.put(`/orders/${o.id}/status`, { status: NEXT[o.status] }); toast.success(`→ ${NEXT[o.status]}`); load(); } catch (e) { toast.error(apiErr(e)); } };
  const setStatus = async (o, status) => { try { await api.put(`/orders/${o.id}/status`, { status }); toast.success(`Status: ${status}`); load(); if (sel) setSel({ ...sel, status }); } catch (e) { toast.error(apiErr(e)); } };

  const doReturn = async (o) => {
    const items = Object.entries(retItems).filter(([, q]) => q > 0).map(([product_id, quantity]) => ({ product_id, quantity: parseInt(quantity) }));
    if (!items.length) return toast.error("Enter return quantities");
    try { await api.post("/returns", { order_id: o.id, items, reason: "admin_return", refund: true }); toast.success("Return processed, stock & sales updated"); setSel(null); setRetItems({}); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-5">
      <h1 className="text-2xl font-bold">Orders</h1>
      <div className="flex gap-2">
        {[["all", "All"], ["online", "Online"], ["pos", "POS"]].map(([k, l]) => (
          <button key={k} data-testid={`orders-tab-${k}`} onClick={() => setTab(k)} className={`px-4 py-2 rounded-lg text-sm font-bold ${tab === k ? "bg-emerald-600 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>{l}</button>
        ))}
      </div>
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]">
          <thead><tr className="text-xs font-bold uppercase tracking-wider text-slate-500 bg-slate-50">
            <th className="text-left py-3 px-4">Order</th><th className="text-left py-3 px-4">Type</th><th className="text-left py-3 px-4">Customer</th><th className="text-left py-3 px-4">Total</th><th className="text-left py-3 px-4">Status</th><th className="text-right py-3 px-4">Action</th></tr></thead>
          <tbody>{orders.map((o) => (
            <tr key={o.id} data-testid={`admin-order-${o.id}`} className="border-b border-slate-50 hover:bg-slate-50">
              <td className="py-2.5 px-4"><button onClick={() => setSel(o)} className="font-semibold text-emerald-700 hover:underline">{o.order_no}</button><p className="text-xs text-slate-400">{new Date(o.created_at).toLocaleDateString("en-IN")}</p></td>
              <td className="py-2.5 px-4"><span className={`text-xs font-bold px-2 py-0.5 rounded ${o.type === "pos" ? "bg-purple-100 text-purple-700" : "bg-indigo-100 text-indigo-700"}`}>{o.type.toUpperCase()}</span></td>
              <td className="py-2.5 px-4">{o.customer_name || "Walk-in"}</td>
              <td className="py-2.5 px-4 font-semibold">{inr(o.total)}</td>
              <td className="py-2.5 px-4"><StatusBadge status={o.status} testid={`admin-order-status-${o.id}`} /></td>
              <td className="py-2.5 px-4 text-right">{NEXT[o.status] && <button data-testid={`advance-${o.id}`} onClick={() => advance(o)} className="px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-bold">→ {NEXT[o.status]}</button>}</td>
            </tr>
          ))}</tbody>
        </table>
      </div>

      <Dialog open={!!sel} onOpenChange={(v) => { if (!v) { setSel(null); setRetItems({}); } }}>
        <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto">
          {sel && <>
            <DialogHeader><DialogTitle className="flex items-center gap-2">{sel.order_no} <StatusBadge status={sel.status} /></DialogTitle></DialogHeader>
            <div className="text-sm space-y-2">
              <p className="text-slate-500">{sel.customer_name} {sel.customer_phone && `· ${sel.customer_phone}`} · {sel.payment_method?.toUpperCase()} ({sel.payment_status})</p>
              {sel.address && <p className="text-slate-500 text-xs bg-slate-50 rounded-lg p-2">{sel.address.line1}, {sel.address.city}, {sel.address.state} - {sel.address.pincode}</p>}
              <div className="border rounded-lg divide-y">{sel.items.map((it) => (
                <div key={it.product_id} className="flex items-center justify-between p-2">
                  <div className="flex items-center gap-2"><img src={it.image} alt="" className="w-8 h-8 rounded object-cover" /><span>{it.name} × {it.quantity}</span></div>
                  <div className="flex items-center gap-2"><span className="font-semibold">{inr(it.line_total)}</span>
                    <input data-testid={`return-qty-${it.product_id}`} type="number" min="0" max={it.quantity} placeholder="ret" value={retItems[it.product_id] || ""} onChange={(e) => setRetItems({ ...retItems, [it.product_id]: e.target.value })} className="w-14 border rounded px-1.5 py-1 text-xs" /></div>
                </div>
              ))}</div>
              <div className="flex justify-between font-bold"><span>Total</span><span>{inr(sel.total)}</span></div>
              {subs.length > 1 && (
                <div data-testid="admin-suborders" className="border border-orange-100 rounded-lg p-2 bg-orange-50/50 space-y-1.5">
                  <p className="text-xs font-bold text-orange-700 uppercase tracking-wide">Seller Split ({subs.length})</p>
                  {subs.map((sb) => (
                    <div key={sb.id} data-testid={`admin-suborder-${sb.id}`} className="flex items-center justify-between text-xs bg-white rounded-lg px-2 py-1.5 border border-orange-100">
                      <span className="truncate"><span className="font-semibold">{sb.is_house ? "🏬 " : "🛍️ "}{sb.seller_name}</span> · {sb.items.length} item{sb.items.length > 1 ? "s" : ""} · {inr(sb.subtotal)}</span>
                      <StatusBadge status={sb.status} testid={`admin-suborder-status-${sb.id}`} />
                    </div>
                  ))}
                </div>
              )}
              <div><label className="text-xs font-semibold text-slate-500">Set Status</label>
                <div className="flex flex-wrap gap-1.5 mt-1">{ALL.map((s) => <button key={s} data-testid={`set-status-${s.replace(/ /g,'-')}`} onClick={() => setStatus(sel, s)} className={`text-xs px-2 py-1 rounded border ${sel.status === s ? "bg-emerald-600 text-white border-emerald-600" : "bg-white border-slate-200"}`}>{s}</button>)}</div>
              </div>
              <button data-testid="process-return-btn" onClick={() => doReturn(sel)} className="w-full mt-2 py-2 border border-rose-200 text-rose-600 rounded-lg font-bold text-sm hover:bg-rose-50">Process Return / Refund</button>
            </div>
          </>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
